import { useState } from "react";
import { ArrowLeft, AlertCircle, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { BUDGETS, userTz } from "@/lib/format";
import { COUNTRIES } from "./countries";
import { BookingSummary } from "./BookingSummary";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const WA_RE = /^\+[1-9]\d{6,14}$/;

function validate(f) {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (!WA_RE.test(f.whatsapp.replace(/[\s\-()]/g, ""))) e.whatsapp = "Include your country code, e.g. +251 91 234 5678.";
  if (f.country.trim().length < 2) e.country = "Please enter your country of residence.";
  return e;
}

function Field({ id, label, optional, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-[#172033] mb-2">
        {label} {optional && <span className="text-[#697386] font-normal">(optional)</span>}
      </label>
      {children}
      {error && <p id={`${id}-error`} className="mt-1.5 text-xs text-[#C2410C]" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
}

export function RegistrationForm({ form, setForm, slot, duration, onBack, onPickAnother, onSuccess }) {
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [conflict, setConflict] = useState(false);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const errs = validate(form);
    setErrors(errs);
    setServerError(null);
    setConflict(false);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      const payload = { ...form, start_utc: slot.start_utc, visitor_timezone: userTz, budget: form.budget || null, question: form.question || null };
      const { data } = await api.post("/bookings", payload);
      onSuccess(data);
    } catch (err) {
      setServerError(formatApiError(err));
      setConflict(err.response?.status === 409);
      setSubmitting(false);
    }
  };

  const inputProps = (k) => ({ id: k, value: form[k], onChange: set(k), className: "field", "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${k}-error` : undefined, "data-testid": `booking-input-${k}` });

  return (
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12" data-testid="registration-form-step">
      <div className="lg:col-span-4">
        <button onClick={onBack} className="text-sm text-[#697386] hover:text-[#0B1F3A] flex items-center gap-2 transition-colors" data-testid="booking-back-button">
          <ArrowLeft size={15} /> Change time
        </button>
        <BookingSummary startUtc={slot.start_utc} duration={duration} />
      </div>
      <form onSubmit={submit} noValidate className="lg:col-span-8 space-y-5" data-testid="registration-form">
        <div>
          <h3 className="font-display text-2xl font-semibold text-[#172033]">Tell us about your goals.</h3>
          <p className="mt-1 text-sm text-[#697386]">Takes about a minute. Only what’s needed to manage your booking.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field id="full_name" label="Full name" error={errors.full_name}><input {...inputProps("full_name")} autoComplete="name" /></Field>
          <Field id="email" label="Email address" error={errors.email}><input {...inputProps("email")} type="email" autoComplete="email" /></Field>
          <Field id="whatsapp" label="WhatsApp number" error={errors.whatsapp}><input {...inputProps("whatsapp")} type="tel" placeholder="+251 91 234 5678" autoComplete="tel" /></Field>
          <Field id="country" label="Country of residence" error={errors.country}>
            <input {...inputProps("country")} list="country-list" autoComplete="country-name" />
            <datalist id="country-list">{COUNTRIES.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
        </div>
        <Field id="budget" label="Investment budget (USD)" optional>
          <Select value={form.budget} onValueChange={set("budget")}>
            <SelectTrigger id="budget" className="field h-auto" data-testid="booking-input-budget"><SelectValue placeholder="Select a range" /></SelectTrigger>
            <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b} data-testid={`budget-option-${BUDGETS.indexOf(b)}`}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <p className="mt-1.5 text-xs text-[#697386]">Example ranges only — there is no minimum to attend.</p>
        </Field>
        <Field id="question" label="Your question or investment interest" optional>
          <textarea {...inputProps("question")} rows={3} maxLength={1500} className="field resize-none" />
        </Field>
        <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

        {serverError && (
          <div className="flex gap-3 rounded-2xl border border-[#C2410C]/25 bg-[#C2410C]/5 p-4 text-sm text-[#172033]" role="alert" data-testid="booking-server-error">
            <AlertCircle size={18} className="text-[#C2410C] shrink-0" />
            <div>
              <p>{serverError}</p>
              {conflict && <button type="button" onClick={onPickAnother} className="mt-2 font-semibold text-[#0B1F3A] underline underline-offset-4" data-testid="booking-pick-another-button">Choose another time</button>}
            </div>
          </div>
        )}

        <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-4">
          <button type="submit" disabled={submitting} className="btn-primary sm:min-w-[220px]" data-testid="booking-submit-button">
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Confirming…</> : "Confirm booking"}
          </button>
          <p className="text-xs text-[#697386]">Your details are used only to manage this briefing. See our privacy policy in the footer.</p>
        </div>
      </form>
    </div>
  );
}
